import { Injectable} from '@angular/core';
import {  of, forkJoin  } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import {IspmHttpClient} from '@ispm/core/services/ispm-http-client';
import {filter, map, take, tap,catchError,finalize } from "rxjs/operators";
import {FormHandlerService} from "@ispm/dynamic-form/services/form-handler.service";
import {find, get, isEmpty, set} from 'lodash';
import {GetModelContextService} from "@ispm-client/modules/selection-item/services/get-model-context.service";
import {Store} from "@ngrx/store";
import {
  IitemsState,
  ItreeItemsWithIntent,
  ItypeTreeList
} from "@ispm-client/modules/selection-item/interfaces/floatingPanel.interface";
import {BundleCall} from "@ispm-client/modules/aggregator/services/bundleCall.service";
import {SearchService} from "@ispm/dynamic-form/services/search.service";
import {ITEM_ACTIONS} from "@ispm-client/modules/selection-item/store/actions";
import {WorkflowUtilService} from '@ispm-client/modules/aggregator/services/workflow-util.service';
import * as immutable from "immutable";
import {selectActiveWorkflow} from "@ispm/workflow/store/workflow-config.selectors";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {InterpolateService} from "@ispm/shared/services/interpolate.service";
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Injectable()
export class GetItemsDataService {
  public store$: Store<IitemsState>;
  private cachedQuery: Map<string, any> = new Map();

  constructor(private _ispmHttpClient: IspmHttpClient,
              private _formHandlerService: FormHandlerService,
              private authService: AuthenticationService,
              private _getModelContextService: GetModelContextService,
              private store: Store<IitemsState>,
              private _bundleCall: BundleCall,
              private _searchService: SearchService,
              private _workflowUtilService: WorkflowUtilService,
              private storeWorkflow: Store<IgenomicsState>,
              private interpolator: InterpolateService,
              private routerManagerService: RouterManagerService) {
    this.store$ = store.select((state: any) => state.treeItems) as any;
  }

  /**
   * get tree data of specific intent
   * @returns {Observable<immutable.Map<string, ItypeTreeList>>}
   */
  public getAllTreeItems(): Observable<immutable.Map<string, ItypeTreeList>> {
    return this.store$.select('treeItemsList');
  }

  /**
   * Store the tree data of specific intent
   * @param {ItreeItemsWithIntent} itemsList
   */
  public setAllTreeItem(itemsList: ItreeItemsWithIntent) {
    this.store$.dispatch({
      type: ITEM_ACTIONS.ADD_ITEMS,
      payload: itemsList
    });
  }

  /**
   * gets data for the attributes additionalNotesDataBinding and itemsDataBinding
   * @param itemsPayload
   * @param addiConfigPayload
   * @returns {Observable<any>}
   */
  public getAllItems(itemsPayload: any, addiConfigPayload: any): Observable<any> {
    const itemsQuery = get(itemsPayload,'queries.0.queryString');
    const additionalQuery = get(addiConfigPayload,'queries.0.queryString');
    const itemsPayloadResponse = this.cachedQuery.get(itemsQuery);
    const addiConfigPayloadResponse = this.cachedQuery.get(additionalQuery);
    let itemsPayloadPromise: Observable<any> = this._bundleCall.searchCall(itemsPayload).pipe(
      tap((response) => {
        this.cachedQuery.set(itemsQuery, response)
      }),
      catchError((error) => {
        console.error('Error fetching itemsPayload:', error);
        return of(null);
      })
    ).take(1);

    let addiConfigPayloadPromise: Observable<any> = this._bundleCall.searchCall(addiConfigPayload).pipe(
      tap((response) => {
          this.cachedQuery.set(additionalQuery, response)
        }),
      catchError((error) => {
        console.error('Error fetching addiConfigPayload:', error);
        return of(null);
      })
    ).take(1);
    if(!isEmpty(itemsPayloadResponse)){
      itemsPayloadPromise = of(itemsPayloadResponse);
    }
    if(!isEmpty(addiConfigPayloadResponse)){
      addiConfigPayloadPromise = of(addiConfigPayloadResponse);
    }
    return forkJoin(itemsPayloadPromise,addiConfigPayloadPromise);
  }

  /**
   * Gets workflow testType
   * @returns {Promise<any>}
   */
  public getWorkflowTestType(): Promise<any> {
    return this.storeWorkflow.select(selectActiveWorkflow).pipe(take(1)).toPromise();
  }

  /**
   * gets data using attribute form options resource
   * @param {string} resource
   * @param context
   * @param addiConfigPayload
   * @param attributes
   * @returns {Observable<any>}
   */
  public getOptionsResourceItems(resource: string, context: any, addiConfigPayload: any, attributes: any): Observable<any> {
    return Observable.forkJoin(this._searchService.query(resource, '', context,  attributes).take(1),
      this._bundleCall.searchCall(addiConfigPayload).take(1))
  }

  getFormIdentifier(formId): Observable<any> {
    return this._formHandlerService.getStoredFormConfigs().pipe(
      map(formConfigs => {
        if (formConfigs.has(formId)) {
          return formConfigs.get(formId);
        } else {
          this._formHandlerService.getForm(formId);
          return null;
        }
      }),
      filter(config => !!config),
      take(1)
    );
  }

  /**
   * form a query
   * @param {Array<any>} itemsDataBinding
   * @param {string} identifier
   * @param {string} intentValue
   * @param {boolean} items
   * @param intentKey
   * @returns {any}
   */
  formQuery(itemsDataBinding: Array<any>, searchObject = {}): any {
     let queryString = null;
     if(itemsDataBinding) {
       searchObject['searchTerm'] = searchObject;
       queryString = this.interpolator.interpolate(get(itemsDataBinding, [0, 'queryString']), searchObject, true);
     }
     return {'queries': set(itemsDataBinding, [0,'queryString'], queryString)};
  }

  /**
   * Permission for read/write access based on role
   */
  permissions(widgetPayload, typeOfAccess): boolean {
    let permitItemSelectionWrite = false;
    let activeJobPurpose = "";
    let requiredPermissionsIds = get(widgetPayload, 'requiredPermissionsIds');
    activeJobPurpose = this.getActiveJobPurpose();
    activeJobPurpose = activeJobPurpose.replace(/\s/g, "").toUpperCase();
    if (requiredPermissionsIds) {
      permitItemSelectionWrite = this.authService.doesUserHasPermission(typeOfAccess);
    }

    if(get(widgetPayload, 'permitOnlyAssignee')) {
      this._workflowUtilService.checkIfPerformer().subscribe(isPerformer => {
        permitItemSelectionWrite = permitItemSelectionWrite && isPerformer;
      });
    }

    if(get(widgetPayload, 'readOnlyMode') && permitItemSelectionWrite === true) {
      let fetchStageData = this.routerManagerService.queryParameters.payload;
      permitItemSelectionWrite = fetchStageData.toUpperCase() === activeJobPurpose ? true : false;
    }
    return permitItemSelectionWrite;
  }

  /**
   get active workflow step
   */
  getActiveJobPurpose(): string {
    let activeJobPurpose = "";
    this._getModelContextService.getDatamodelAttributes().subscribe( data => {
      if(!isEmpty(data)) {
        let activeRevisionItem = find(get(data, 'workflowRequest.revisions'), ['id', get(data, 'workflowRequest.activeRevision')]);
        let activeJobItem = find(get(activeRevisionItem,'items'), ['id', get(data, 'workflowRequest.activeJob')]);
        activeJobPurpose = get(activeJobItem, 'purpose', '');
      }
    });
    return activeJobPurpose;
  }

}
